// Copyright (c) 2019-present Ithpower, Inc. All Rights Reserved.
// See LICENSE.txt for license information.
import React from 'react';
import {message} from 'antd';
import {Location, History} from 'history';
import Cookies from 'js-cookie';

import synchronizingImage from 'images/login/synchronizing.gif';
import {getWeComCorpIdByCode, getWeComCorpIdByAuthCode, loadWeComEnterpriseId, loadWeComSyncStatus, loginByWeComOauth} from 'server/actions/users';
import {loadEnterpriseAdmins} from 'server/actions/staffs';
import {IsvType} from 'types/users';
import GlobalContext from 'context/global_context';

import {setClientSingalByCookies} from 'utils/utils';
import {OrganizationSyncStatus} from 'utils/constants/general';

import TripartiteAuthBase, {AuthBaseProps} from './tripartite_auth_base';

const cookieNameToken = 'IAIAUTHTOKEN';

class WecomSSO extends TripartiteAuthBase {
    userId: string | undefined;
    authCode: string | null | undefined;

    constructor(props: AuthBaseProps) {
        super(props);

        const search = new URLSearchParams(props.location.search);
        this.code = search.get('code');
        this.authCode = search.get('auth_code'); //单点登录code
        this.isvAppType = search.get('state') as IsvType;
        this.authToken = Cookies.get(cookieNameToken);
    }

    componentDidMount() {
        if ((!this.code || !this.isvAppType) && !this.authCode) {
            this.handleAuthInvalid();
            return;
        }

        // 使用 code或authCode 换取 corpId
        this.getCorpIdPromise().then(({data}) => {
            if (data) {
                const {corp_id, user_id} = data;
                this.corpId = corp_id;
                this.userId = user_id;

                // 授权登录拿到数据后还原AppType
                if (this.isvAppType && (this.isvAppType as string) === 'WWLogin') {
                    this.isvAppType = 'Performance';
                }

                if (this.authToken) {
                    this.verifyEnterpriseInfo();
                } else {
                    this.checkOrganizationSyncStatus();
                }
            }
        });
    }

    getCorpIdPromise = () => {
        if (this.code && this.isvAppType) {
            return getWeComCorpIdByCode(this.code, this.isvAppType);
        }
        if (this.authCode) {
            return getWeComCorpIdByAuthCode(this.authCode);
        }
        return Promise.reject('No valid code');
    };
    

    reTry = () => {
        this.handleStatusInit();
        this.checkOrganizationSyncStatus();
    };

    checkOrganizationSyncStatus = () => {
        if (this.corpId) {
            loadWeComSyncStatus(this.corpId).then(({data}) => {
                if (data) {
                    if (data.status === OrganizationSyncStatus.COMPLETED) {
                        this.ssoLogin();
                    } else if (data.status === OrganizationSyncStatus.SYNC) {
                        this.handleSynchronizing();
                        if (this.syncTimer) {
                            clearTimeout(this.syncTimer);
                        }
                        this.syncTimer = setTimeout(this.checkOrganizationSyncStatus, 5000);
                    }
                }
            });
        }
    };

    ssoLogin = () => {
        if (this.userId && this.corpId && this.isvAppType) {
            loginByWeComOauth({
                user_id: this.userId,
                corp_id: this.corpId,
                isv_app_type: this.isvAppType,
            }).then(({data, error}) => {
                if (data) {
                    this.props.setUserInfo?.(data);
                } else if (error) {
                    if (error.server_error_id === 'no_ding_talk_auth') {
                        const appName = this.isvAppType === 'Performance' ? '智办事绩效' : '互成协作';
                        this.settingErrorMessage(`你还没有开通应用的使用权限，请联系企业管理员在"工作台-全员-${appName}-可见范围"帮你开通。`);
                        this.handleAuthError();
                    } else {
                        this.handleAuthError();
                    }
                    return;
                }
                this.finishSignin();
            });
        }
    };

    finishSignin = () => {
        setClientSingalByCookies();
        this.verifyEnterpriseInfo();
    };

    verifyEnterpriseInfo = async () => {
        // 登录完成后SESSION_COOKIE_ISVAPPTYPE会被后端写入
        if (!this.corpId || !this.isvAppType) {
            return;
        }

        let enterpriseId: string | undefined;
        if (typeof document !== 'undefined' && typeof document.cookie !== 'undefined') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.startsWith('IAIENTERPRISEID=')) {
                    enterpriseId = cookie.replace('IAIENTERPRISEID=', '');
                    break;
                }
            }
        }
        if (!enterpriseId) {
            message.error('登录失败，请尝试重新登录');
            return;
        }
        const checkEnterprise = await loadWeComEnterpriseId(this.corpId).then(({error, data}) => {
            if (error) {
                if (error.server_error_id === 'invalid_corp_id') {
                    // 企业id不存在，统一认为是服务端正在同步架构
                    return 'sync';
                }
                return 'error';
            } else if (data && data.enterprise_id === enterpriseId) {
                return 'correct';
            }
            return 'imparity';
        });
        if (checkEnterprise === 'correct') {
            loadEnterpriseAdmins().then(({error, data}) => {
                if (error) {
                    setTimeout(this.verifyEnterpriseInfo, 5000);
                } else if (data && data.length !== 0) {
                    this.redirectToHomePage();
                } else {
                    this.props.history?.replace('/admin_setting');
                }
            });
        } else if (checkEnterprise === 'imparity') {
            this.ssoLogin();
        } else if (checkEnterprise === 'sync') {
            this.checkOrganizationSyncStatus();
        } else {
            message.error('登录失败，请尝试重新登录');
        }
    };

    renderSynchronizing = () => {
        return (
            <div className={'tripartite-app-placeholder-wrapper'}>
                <img
                    className='loading-image'
                    src={synchronizingImage}
                    alt='loading'
                />
                <div className={'tripartite-app-placeholder-text'}>
                    <div className={'tripartite-app-placeholder-title'}>
                        {'稍等一下，正在为你同步企业通讯录'}
                    </div>
                    <div className={'tripartite-app-placeholder-tip'}>
                        {'同步过程预计需要1-2分钟...'}
                    </div>
                </div>
            </div>
        );
    };
}

class WrapperContext extends React.PureComponent<{location: Location, history: History}> {
    render() {
        return (
            <GlobalContext.Consumer>
                {({clientConfig, setUserInfo}) =>
                    (<WecomSSO
                        location={this.props.location}
                        history={this.props.history}
                        clientConfig={clientConfig}
                        setUserInfo={setUserInfo}
                    />)
                }
            </GlobalContext.Consumer>
        );
    }
}

export default WrapperContext;
